import { useEffect, useState } from "react";
import {
  TabList,
  Tab,
  SelectTabEvent,
  SelectTabData,
  TabValue,
  Spinner,
  makeStyles,
  Button
} from "@fluentui/react-components";
import "./dashboard.css";
import { FoundView as FoundWidget } from "../widgets/FoundWidget";
import CreateTime from "../components/CreateTime";
import ConfigService from "../services/configService";
import SoftTextService from "../services/softTextService";
import DayViewWidget from "../widgets/DayViewWidget";
import { CONSTANTS } from "../constants/constants";
import SecurityService from "../services/securityService";
import { UserLicenses } from "../enums/userLicences";
import SettingsWidget from "../widgets/SettingsWidget";
import LoggingService from "../services/loggingService";
import { MessageType } from "../enums/messageType";
import { useAuth } from "../contexts/AuthContext";
import JwtHelperService from "../services/jwtHelperService";
import { useNavigate } from "react-router-dom";
import { NotificationMessage, NotificationMessageHandler } from "../components/NotificationMessage";
import React from "react";
import { HostClientType } from "@microsoft/teams-js";
import { IdentityProvider } from "../enums/identityProvider";
import SessionService from "../services/sessionService";

const useStyles = makeStyles({
  spinner: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(0, 0, 0, 0.3)",
  },
  noLicense: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '70%'
  }
});

export default function Dashboard() {

  const styles = useStyles();

  // Used to access the token
  const auth = useAuth();

  // Used to call navigate method
  const navigate = useNavigate();

  // This is used to get the notification ref
  const notificationRef = React.useRef<NotificationMessageHandler>(null);

  // It will hold current tab selected
  const [selectedValue, setSelectedValue] = useState<TabValue>(CONSTANTS.create);

  // Is code inside useEffect completed?
  const [isLoaded, setIsLoaded] = useState(false);

  // Does the user have access to use the application
  const [hasAccess, setHasAccess] = useState<boolean>();

  // This function will be called when a tab is selected
  const onTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - onTabSelect(${JSON.stringify(data)}) started` : '', MessageType.Info);
    setSelectedValue(data.value);
    LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - onTabSelect(${JSON.stringify(data)}) - ended` : '', MessageType.Info);
  };

  useEffect(() => {
    try {      
      LoggingService.log(LoggingService.isLoggingEnabled() ? 'Dashboard - useEffect() started' : '', MessageType.Info);

      // Keep validating the token after every 1 minute
      const interval = setInterval(() => {
        validateToken();
      }, 60000);

      SecurityService.loadPermissionsAndLicenses();
      let hasCDTimeLicense = SecurityService.hasLicense(UserLicenses.CDTIME);
      let isSuperAdmin = SecurityService.isSuperAdmin();
      let canUseApplication = hasCDTimeLicense && !isSuperAdmin;
      setHasAccess(canUseApplication);
      if (canUseApplication) {
        loadConfigurationData();
      }
      
      return () => {
        clearInterval(interval);
      };
    } catch (error) { 
      LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - useEffect() - ${error}` : '', MessageType.Error);
    } finally {
      LoggingService.log(LoggingService.isLoggingEnabled() ? 'Dashboard - useEffect() ended' : '', MessageType.Info);
    }
  }, []);

  // Load configuration data, user configuration data and soft text details
  const loadConfigurationData = async () => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? 'Dashboard - loadConfigurationData() started' : '', MessageType.Info);
    await Promise.all([
      ConfigService.loadConfigurationData(),
      ConfigService.loadUserConfigurationData()
    ]);

    SoftTextService.loadAllSoftTextDetails();
    setIsLoaded(true);
    LoggingService.log(LoggingService.isLoggingEnabled() ? 'Dashboard - loadConfigurationData() ended' : '', MessageType.Info);
  };

  // Checks if the token has expired
  function validateToken() {
    console.log('[Dashboard] validateToken() started');
    LoggingService.log(LoggingService.isLoggingEnabled() ? 'Dashboard - validateToken() started' : '', MessageType.Info);
    
    let tokenToCheck: string | null = null;
    const identityProvider = sessionStorage.getItem("IdentityProvider") as IdentityProvider;
    console.log('[Dashboard] IdentityProvider:', identityProvider);
    
    if (identityProvider === IdentityProvider.PI) {
      tokenToCheck = sessionStorage.getItem("PI-X-Auth-Token");
      console.log('[Dashboard] PI token found:', !!tokenToCheck, tokenToCheck ? `Length: ${tokenToCheck.length}` : 'null');
      
      if (tokenToCheck) {
        const decodedJwt = JwtHelperService.parseJwt(tokenToCheck);
        console.log('[Dashboard] Decoded JWT:', decodedJwt ? 'Success' : 'Failed', decodedJwt ? `Exp: ${new Date(decodedJwt.exp * 1000).toISOString()}` : '');
        
        if (decodedJwt != null) {
          const expiryDate = decodedJwt.exp * 1000;
          const now = Date.now();
          const diff = Math.round((expiryDate - now) / 1000);
          
          const isExpired = expiryDate < now;
          console.log('[Dashboard] PI Token Status - IsExpired:', isExpired, 'Seconds until expiry:', diff);
          
          if (!isExpired && diff <= 75) {
            // Proactive refresh: refresh if token expires in <= 75 seconds
            console.log('[Dashboard] PI token expiring in', diff, 'seconds - triggering proactive refresh');
            LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - PI token expiring in ${diff} seconds, refreshing proactively` : '', MessageType.Info);
            let refreshToken = sessionStorage.getItem("refreshToken");
            console.log('[Dashboard] RefreshToken found:', !!refreshToken, refreshToken ? `Length: ${refreshToken.length}` : 'null');
            if (refreshToken) {
              console.log('[Dashboard] Calling getAccessTokenFromRefreshToken...');
              auth.getAccessTokenFromRefreshToken(refreshToken).then(async (result) => {
                console.log('[Dashboard] getAccessTokenFromRefreshToken result:', result);
                if (!result) {
                  console.log('[Dashboard] Refresh failed, falling back to PI discover');
                  await fallbackToPISilentReauth();
                }
              }).catch(async (error) => {
                console.error('[Dashboard] getAccessTokenFromRefreshToken error:', error);
                await fallbackToPISilentReauth();
              });
            } else {
              console.warn('[Dashboard] No refreshToken available for proactive refresh');
              fallbackToPISilentReauth();
            }
          } else if (isExpired) {
            // Token already expired, try to refresh
            console.log('[Dashboard] PI token has expired - attempting refresh');
            LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - PI token has expired, attempting refresh` : '', MessageType.Info);
            let refreshToken = sessionStorage.getItem("refreshToken");
            console.log('[Dashboard] RefreshToken found:', !!refreshToken, refreshToken ? `Length: ${refreshToken.length}` : 'null');
            if (refreshToken) {
              console.log('[Dashboard] Calling getAccessTokenFromRefreshToken...');
              auth.getAccessTokenFromRefreshToken(refreshToken).then(async (result) => {
                console.log('[Dashboard] getAccessTokenFromRefreshToken result:', result);
                if (!result) {
                  console.log('[Dashboard] Refresh failed, falling back to PI discover');
                  await fallbackToPISilentReauth();
                }
              }).catch(async (error) => {
                console.error('[Dashboard] getAccessTokenFromRefreshToken error:', error);
                await fallbackToPISilentReauth();
              });
            } else {
              console.error('[Dashboard] No refreshToken available - redirecting to login');
              LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - No refresh token available, redirecting to login` : '', MessageType.Error);
              notificationRef.current?.showNotification(CONSTANTS.tokenExpiredMessage, true, 5000);
              setTimeout(() => {
                navigate("/loginRedirect");
              }, 5000);
            }
          } else {
            console.log('[Dashboard] PI token still valid, no refresh needed. Expires in', diff, 'seconds');
          }
        } else {
          console.error('[Dashboard] Failed to decode PI token');
        }
      } else {
        console.warn('[Dashboard] No PI token found in sessionStorage');
      }
    } else {
      // IDS - reactive refresh (only when expired)
      console.log('[Dashboard] IDS provider - checking TC token');
      tokenToCheck = auth.userToken || null;
      
      if (tokenToCheck) {
        const decodedJwt = JwtHelperService.parseJwt(tokenToCheck);
        if (decodedJwt != null && decodedJwt.exp * 1000 < Date.now()) { 
          console.log('[Dashboard] IDS token expired');
          LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - token has expired` : '', MessageType.Info);
          let hostClientType = sessionStorage.getItem('hostClientType');
          if (hostClientType !== HostClientType.web) {
            let refreshToken = sessionStorage.getItem("refreshToken");
            if (refreshToken) {
              auth.getAccessTokenFromRefreshToken(refreshToken);
            }
          } else {
            LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - Redirecting user to login redirect screen as token has expired` : '', MessageType.Info);
            notificationRef.current?.showNotification(CONSTANTS.tokenExpiredMessage, true, 5000);
            setTimeout(() => {
              // navigate after 5 seconds
              navigate("/loginRedirect");
            }, 5000)
          }
        }
      }
    }
    console.log('[Dashboard] validateToken() completed');
  }

  async function fallbackToPISilentReauth() {
    try {
      const provider = sessionStorage.getItem("IdentityProvider") as IdentityProvider;
      if (provider !== IdentityProvider.PI) {
        console.log('[Dashboard] Fallback: not PI provider, skipping PI discover');
        return;
      }

      const email = sessionStorage.getItem("UserName") || '';
      let orgReference = sessionStorage.getItem("UserOrgReference") || '';
      console.log('[Dashboard] Fallback PI - email:', email || 'not set', 'orgReference:', orgReference || 'not set');

      if (!orgReference && email) {
        try {
          const firmDetail = await SessionService.getFirmDetail(email);
          orgReference = firmDetail.OrgReference || '';
          console.log('[Dashboard] Fallback PI - discovered orgReference:', orgReference || 'not found');
        } catch (e) {
          console.error('[Dashboard] Fallback PI - firm discovery failed:', e);
        }
      }

      if (email && orgReference) {
        console.log('[Dashboard] Fallback PI - redirecting to Platform Identity discover');
        const success = await auth.redirectToPlatformIdentity(email, orgReference);
        if (!success) {
          console.warn('[Dashboard] Fallback PI - redirectToPlatformIdentity failed, sending to loginRedirect');
          navigate('/loginRedirect');
        }
      } else {
        console.warn('[Dashboard] Fallback PI - insufficient data, sending to loginRedirect');
        navigate('/loginRedirect');
      }
    } catch (e) {
      console.error('[Dashboard] Fallback PI - error:', e);
      navigate('/loginRedirect');
    }
  }

  const handleLogout = () => {
    LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - Logout event clicked` : '', MessageType.Info);
    auth.logout();
    LoggingService.log(LoggingService.isLoggingEnabled() ? `Dashboard - Navigating to login after logout click` : '', MessageType.Info);
    navigate("/tab");
  };

  return (
    hasAccess ? (
      isLoaded ? (
        <div className="welcome page">
    <NotificationMessage ref={notificationRef}/>
    <div className="narrow">
        <div className="tabList">
            <div className="display-flex">
                <div className="w-90-pr">
                    <TabList
                        selectedValue={selectedValue}
                        onTabSelect={onTabSelect}
                        size="large"
                        appearance="subtle"
                    >
                        <Tab id="Create" value={CONSTANTS.create}>
                            Create
                        </Tab>
                        <Tab id="Found" value={CONSTANTS.found}>
                            Found
                        </Tab>
                        <Tab id="Today" value={CONSTANTS.today}>
                            Today 
                        </Tab>
                        <Tab id="Settings" value={CONSTANTS.settings}>
                            Settings
                        </Tab>
                    </TabList>
                </div>
                <div className="w-10-pr">
                    <Button type="button" appearance="primary" className="center-button" onClick={handleLogout}>Logout</Button>
                </div>
            </div>
            <div>
                {selectedValue === CONSTANTS.create && (
                    <div className="cardWidget">
                        <CreateTime />
                    </div>
                )}
                {selectedValue === CONSTANTS.found && (
                    <div>
                        <FoundWidget />
                    </div>
                )}
                {selectedValue === CONSTANTS.today && (
                    <div>
                        <DayViewWidget />
                    </div>
                )}
                {selectedValue === CONSTANTS.settings && (
                    <div>
                        <SettingsWidget />
                    </div>
                )}
            </div>
        </div>
    </div>
</div>

      ) : (
        <div className={styles.spinner}> 
          <Spinner size="large" labelPosition="below" label="Loading..." /> 
        </div>
      )
    ) : (
      <div className={styles.noLicense}>You do not have the license to use this application.</div>
    )
  );
}
